/**
 * VoiceOrb — Universal voice conversation trigger & visualisation.
 *
 * Three size variants:
 *   • `featured`  — Large orb for the Interview feature (primary input).
 *   • `inline`    — Small mic button that fits into PromptInputTools (Chat, Ruleset).
 *   • `compact`   — Minimal icon button for tight spaces (HelpFab).
 *
 * Five visual states derived from `VoiceStatus`:
 *   idle → connecting → listening → thinking → speaking
 *
 * Animations are CSS-only with `prefers-reduced-motion` guard.
 * Accessibility: aria-labels per state (German), keyboard navigation,
 * focus-visible ring.
 *
 * DevPlan 0053 — Tasks 1.b / 1.c / 1.d
 */

import {
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type { VoiceStatus } from '@/hooks/useVoiceConversation';

// ── Types ─────────────────────────────────────────────────────────────────

export type VoiceOrbVariant = 'featured' | 'inline' | 'compact';

export interface VoiceOrbProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'onError'> {
  /** Visual size variant. */
  variant?: VoiceOrbVariant;

  /** Where the expanded panel appears relative to the orb. */
  panelPosition?: 'below' | 'above';

  /** Whether to show the built-in expanded panel. Set false to render controls externally. */
  showPanel?: boolean;

  /** Current voice session status (controlled externally). */
  status?: VoiceStatus;

  /** Whether the microphone is muted (manual mute toggle). */
  isMuted?: boolean;

  /** Live transcript text shown in the expanded panel. */
  liveTranscript?: string;

  // ── Callbacks ──────────────────────────────────────────────────────────

  /** Called when user clicks the orb to start a session. */
  onStart?: () => void;

  /** Called when user clicks the end button. */
  onStop?: () => void;

  /** Called when user toggles the mute button. */
  onMuteToggle?: () => void;

  /** Called when user toggles audio output. */
  onAudioOutputToggle?: () => void;

  /** Whether audio output (TTS) is active. */
  audioOutputEnabled?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────

const STATUS_ARIA_LABELS: Record<VoiceStatus, string> = {
  idle: 'Sprachsitzung starten',
  connecting: 'Aufnahme wird gestartet',
  listening: 'Aufnahme beenden',
  thinking: 'Sprachsitzung beenden',
  speaking: 'Sprachsitzung beenden',
};

/** Ring count per variant. */
const RING_COUNT: Record<VoiceOrbVariant, number> = {
  featured: 3,
  inline: 2,
  compact: 1,
};

// ── Sub-Components ────────────────────────────────────────────────────────

/** Pulsing concentric rings for the "listening" state. */
function PulseRings({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className={cn(
            'pointer-events-none absolute -inset-1 rounded-full border-[3px]',
            'animate-[voice-orb-ping_1.45s_cubic-bezier(0,0,0.2,1)_infinite]',
            'motion-reduce:animate-none',
            className,
          )}
          style={{ animationDelay: `${i * 220}ms` }}
        />
      ))}
    </>
  );
}

/** Wave bars visualisation for the "speaking" state. */
function WaveBars({ barCount = 5, className }: { barCount?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('flex items-center justify-center gap-[2px]', className)}
    >
      {Array.from({ length: barCount }, (_, i) => (
        <span
          key={i}
          className={cn(
            'inline-block w-[3px] rounded-full bg-current',
            'animate-[voice-orb-wave_1s_ease-in-out_infinite]',
            'motion-reduce:animate-none',
          )}
          style={{
            height: '12px',
            animationDelay: `${i * 120}ms`,
          }}
        />
      ))}
    </span>
  );
}

/** Shimmer overlay for the "thinking" state. */
function ThinkingShimmer() {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 overflow-hidden rounded-full',
        'before:absolute before:inset-0',
        'before:animate-[voice-orb-shimmer_1.5s_ease-in-out_infinite]',
        'before:bg-gradient-to-r before:from-transparent before:via-primary/20 before:to-transparent',
        'before:motion-reduce:animate-none',
      )}
    />
  );
}

// ── Main Component ────────────────────────────────────────────────────────

export const VoiceOrb = forwardRef<HTMLDivElement, VoiceOrbProps>(
  function VoiceOrb(
    {
      variant = 'inline',
      panelPosition = 'below',
      showPanel = true,
      status = 'idle',
      isMuted = false,
      liveTranscript,
      audioOutputEnabled = false,
      onStart,
      onStop,
      onMuteToggle,
      onAudioOutputToggle,
      className,
      ...rest
    },
    ref,
  ) {
    const isActive = status !== 'idle';
    const [isExpanded, setIsExpanded] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Auto-expand when session becomes active, collapse on idle.
    useEffect(() => {
      if (isActive && variant === 'featured') {
        setIsExpanded(true);
      } else if (!isActive) {
        setIsExpanded(false);
      }
    }, [isActive, variant]);

    // ── Click / Keyboard handlers ──────────────────────────────────────

    const handleOrbClick = useCallback(() => {
      if (status === 'idle') {
        onStart?.();
        if (variant !== 'featured') setIsExpanded(true);
      } else {
        onStop?.();
        setIsExpanded(false);
      }
    }, [status, onStart, onStop, variant]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOrbClick();
        }
      },
      [handleOrbClick],
    );

    const handleStop = useCallback(() => {
      onStop?.();
      setIsExpanded(false);
    }, [onStop]);

    // ── Orb size classes ───────────────────────────────────────────────

    const orbSizeClass: Record<VoiceOrbVariant, string> = {
      featured: 'h-14 w-14',
      inline: 'h-8 w-8',
      compact: 'h-7 w-7',
    };

    const iconSizeClass: Record<VoiceOrbVariant, string> = {
      featured: 'h-6 w-6',
      inline: 'h-4 w-4',
      compact: 'h-3.5 w-3.5',
    };

    // ── Orb button variant based on status ─────────────────────────────

    const orbVariantClass = cn(
      'relative z-10 rounded-full transition-all duration-300 shrink-0',
      status === 'idle' &&
        'bg-transparent text-muted-foreground border border-border hover:bg-accent hover:text-foreground',
      status === 'connecting' && 'bg-muted text-muted-foreground',
      status === 'listening' &&
        'bg-voice-listening text-voice-listening-foreground ring-2 ring-voice-listening/70 shadow-[0_0_0_4px_hsl(var(--voice-listening)/0.14)] animate-[voice-orb-record-pulse_1.45s_ease-in-out_infinite] motion-reduce:animate-none',
      status === 'thinking' && 'bg-voice-thinking text-voice-thinking-foreground',
      status === 'speaking' && 'bg-voice-speaking text-voice-speaking-foreground',
    );

    // ── Orb icon ───────────────────────────────────────────────────────

    const renderIcon = () => {
      const sizeClass = iconSizeClass[variant];
      switch (status) {
        case 'connecting':
          return (
            <Loader2
              className={cn(sizeClass, 'animate-spin motion-reduce:animate-none')}
            />
          );
        case 'listening':
          return isMuted ? (
            <MicOff className={cn(sizeClass, 'stroke-[2.8]')} />
          ) : (
            <Mic className={cn(sizeClass, 'stroke-[2.8]')} />
          );
        case 'thinking':
          return <Mic className={cn(sizeClass, 'opacity-60')} />;
        case 'speaking':
          return <WaveBars barCount={variant === 'compact' ? 3 : 5} />;
        default:
          return <Mic className={sizeClass} />;
      }
    };

    // ── Render ─────────────────────────────────────────────────────────

    return (
      <div
        ref={ref}
        className={cn(
          'voice-orb relative inline-flex items-center',
          panelPosition === 'above' ? 'flex-col-reverse' : 'flex-col',
          className,
        )}
        {...rest}
      >
        {/* ── Orb button ── */}
        <div className="relative inline-flex items-center justify-center">
          {/* Listening pulse rings */}
          {status === 'listening' && !isMuted && (
            <PulseRings
              count={RING_COUNT[variant]}
              className={cn(orbSizeClass[variant], 'border-voice-listening/45')}
            />
          )}

          {/* Thinking shimmer */}
          {status === 'thinking' && <ThinkingShimmer />}

          <button
            type="button"
            role="button"
            aria-label={STATUS_ARIA_LABELS[status]}
            aria-live="polite"
            className={cn(
              orbSizeClass[variant],
              orbVariantClass,
              'inline-flex items-center justify-center',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            onClick={handleOrbClick}
            onKeyDown={handleKeyDown}
          >
            {renderIcon()}
          </button>
        </div>

        {/* ── Expanded panel (shown when active) ── */}
        {showPanel && isExpanded && isActive && (
          <div
            ref={panelRef}
            role="region"
            aria-label="Sprachsitzungs-Steuerung"
            className={cn(
              panelPosition === 'above' ? 'mb-2' : 'mt-2',
              'flex flex-col gap-2 rounded-lg border border-border bg-background p-3 shadow-md',
              'animate-[voice-orb-expand_200ms_ease-out]',
              'motion-reduce:animate-none',
              variant === 'featured' && 'w-72',
              variant === 'inline' && 'w-60',
              variant === 'compact' && 'w-52',
            )}
          >
            {/* Status indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span
                aria-hidden
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full transition-colors',
                  status === 'connecting' && 'bg-muted-foreground/40',
                  status === 'listening' &&
                    'bg-voice-listening animate-pulse motion-reduce:animate-none',
                  status === 'thinking' &&
                    'bg-voice-thinking animate-pulse motion-reduce:animate-none',
                  status === 'speaking' &&
                    'bg-voice-speaking animate-pulse motion-reduce:animate-none',
                )}
              />
              <span>{STATUS_ARIA_LABELS[status]}</span>
            </div>

            {/* Live transcript */}
            {liveTranscript && (
              <p
                className="max-h-20 overflow-y-auto text-sm text-foreground"
                aria-live="polite"
                aria-label="Live-Transkript"
              >
                {liveTranscript}
              </p>
            )}

            {/* Controls row */}
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1">
                {/* Mute toggle */}
                {onMuteToggle && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onMuteToggle}
                    aria-label={
                      isMuted
                        ? 'Mikrofon aktivieren'
                        : 'Mikrofon stummschalten'
                    }
                    className={cn(isMuted && 'text-destructive')}
                  >
                    {isMuted ? (
                      <MicOff className="h-3.5 w-3.5" />
                    ) : (
                      <Mic className="h-3.5 w-3.5" />
                    )}
                  </Button>
                )}

                {/* Audio output toggle */}
                {onAudioOutputToggle && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onAudioOutputToggle}
                    aria-label={
                      audioOutputEnabled
                        ? 'Sprachausgabe deaktivieren'
                        : 'Sprachausgabe aktivieren'
                    }
                    className={cn(
                      audioOutputEnabled
                        ? 'text-primary'
                        : 'text-muted-foreground/50',
                    )}
                  >
                    {audioOutputEnabled ? (
                      <Volume2 className="h-3.5 w-3.5" />
                    ) : (
                      <VolumeX className="h-3.5 w-3.5" />
                    )}
                  </Button>
                )}
              </div>

              {/* End session */}
              <Button
                variant="destructive"
                size="icon-sm"
                onClick={handleStop}
                aria-label="Sprachsitzung beenden"
              >
                <PhoneOff className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
